import { clampedAlpha } from "./lerp-utils.js";
import * as THREE from "three";
//#region ../src/visualization/alignment-lerper.ts
/**
* Alignment Lerper Module
*
* Smoothly interpolates arWorldGroup.matrix toward a target alignment
* matrix each frame, eliminating visual jumps when the alignment solver
* produces a new alignment (~1 Hz).
*
* Pattern: decompose → lerp position / slerp quaternion → compose.
* Same lerpRate convention as camera-follower.ts (default 8 → ~90%
* convergence in ~0.3 s at 60 fps).
*
* The first target is applied instantly (no animation from identity).
*
* @see gps-plus-slam/GpsPlusSlamJs_Docs/docs/2026-03-21-user-feedback.md Issue 4
*/
const _currentPos = new THREE.Vector3();
const _currentQuat = new THREE.Quaternion();
const _currentScale = new THREE.Vector3();
new THREE.Vector3();
new THREE.Quaternion();
new THREE.Vector3();
/**
* Create an AlignmentLerper that drives arWorldGroup.matrix smoothly.
*
* @param arWorldGroup The group whose matrix is interpolated.
* @param lerpRate     Lerp speed multiplier (default 8).
*/
const CONVERGED_POS_EPSILON_SQ = 1e-4 * 1e-4;
const CONVERGED_QUAT_DOT = .99999999;
const CONVERGED_SCALE_EPSILON_SQ = 1e-6 * 1e-6;
function createAlignmentLerper(arWorldGroup, lerpRate = 8) {
	let hasTarget = false;
	let isFirstTarget = true;
	let converged = false;
	const storedTargetPos = new THREE.Vector3();
	const storedTargetQuat = new THREE.Quaternion();
	const storedTargetScale = new THREE.Vector3(1, 1, 1);
	const _matrix = new THREE.Matrix4();
	/** Write the exact stored target and refresh the subtree. */
	const applyTargetExactly = () => {
		_matrix.compose(storedTargetPos, storedTargetQuat, storedTargetScale);
		arWorldGroup.matrix.copy(_matrix);
		arWorldGroup.matrixAutoUpdate = false;
		arWorldGroup.updateMatrixWorld(true);
	};
	return {
		setTarget(matrix) {
			_matrix.fromArray(matrix);
			_matrix.decompose(storedTargetPos, storedTargetQuat, storedTargetScale);
			hasTarget = true;
			converged = false;
		},
		update(dt) {
			if (!hasTarget || converged) return;
			if (isFirstTarget) {
				applyTargetExactly();
				isFirstTarget = false;
				converged = true;
				return;
			}
			arWorldGroup.matrix.decompose(_currentPos, _currentQuat, _currentScale);
			const alpha = clampedAlpha(lerpRate, dt);
			_currentPos.lerp(storedTargetPos, alpha);
			_currentQuat.slerp(storedTargetQuat, alpha);
			_currentScale.lerp(storedTargetScale, alpha);
			if (_currentPos.distanceToSquared(storedTargetPos) < CONVERGED_POS_EPSILON_SQ && Math.abs(_currentQuat.dot(storedTargetQuat)) > CONVERGED_QUAT_DOT && _currentScale.distanceToSquared(storedTargetScale) < CONVERGED_SCALE_EPSILON_SQ) {
				applyTargetExactly();
				converged = true;
				return;
			}
			_matrix.compose(_currentPos, _currentQuat, _currentScale);
			arWorldGroup.matrix.copy(_matrix);
			arWorldGroup.matrixAutoUpdate = false;
			arWorldGroup.updateMatrixWorld(true);
		},
		dispose() {}
	};
}
//#endregion
export { createAlignmentLerper };
